import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ToastProvider } from "./contexts/ToastContext";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// Auth pages - loaded eagerly for fast signin/registration
import Registration from "./components/Auth/Registration";
import Login from "./components/Auth/Login";
import VerifyEmail from "./components/Auth/VerifyEmail";
import ForgotPassword from "./components/Auth/ForgotPassword";
import OtpVerification from "./components/Auth/OtpVerification";
import ChangePassword from "./components/Auth/ChangePassword";
import PrivateRoute from "./components/PrivateRoute";
import PendingPaymentNotice from "./components/Common/PendingPaymentNotice";

// Eagerly import all CSS so styles load in the same order as before.
// This preserves layout/fonts/styles on every page while allowing JS code splitting.
import "react-time-picker/dist/TimePicker.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "react-datepicker/dist/react-datepicker.css";
import "./components/Layout/Sidebar.css";
import "./components/Layout/Topbar.css";
import "./components/Common/Shimmer.css";
import "./components/Dashboard/Dashboard.css";
import "./components/Setting/Settings.css";
import "./components/Setting/APISettings.css";
import "./components/Setting/Holidays/Holidays.css";
import "./components/Setting/Policy/PolicyForm.css";
import "./components/Setting/TimeOffConfiguration/TimeOffConfiguration.css";
import "./components/Setting/TimeOffConfiguration/TimeOffPolicies.css";
import "./components/Setting/IntutitPayrollConnect/PayrollConnect.css";
import "./components/Setting/IntutitPayrollConnect/TimeLogEntry/TimeLogEntry.css";
import "./components/User/Users.css";
import "./components/User/Profile.css";
import "./components/User/UserAction.css";
import "./components/User/AddExistingUserAction.css";
import "./components/User/VerifyPhoneNo.css";
import "./components/User/Statistics/Statistics.css";
import "./components/Premise/Premises.css";
import "./components/Premise/PremiseAction.css";
import "./components/Report/Report.css";
import "./components/Report/BreakAndViolation/BreakAndViolation.css";
import "./components/Report/BreakAndViolation/BreakAndViolationReport.css";
import "./components/Report/Leave/LeaveReport.css";
import "./components/Report/TimePermission/TimePermissionReport.css";
import "./components/Report/Timing/Timing.css";
import "./components/Report/Timing/TimingReport.css";
import "./components/Report/Timing/HoursWorkedTimingReport.css";
import "./components/Subscription/SubscriptionCheckOut.css";
import "./components/Subscription/SubscriptionPlanDetails.css";
import "./components/Invoice/Payments.css";
import "./components/Invoice/SubscriptionSummary.css";
import "./components/WorkShifts/WorkShiftTemplate.css";
import "./components/WorkShifts/StaffShift.css";
import "./components/WorkLocations/DefineWorkLocation.css";
import "./components/WorkLocations/AssignLocationToStaff.css";
import "./components/AI/AIPage.css";
import "./components/Payroll/Payroll.css";

// Protected pages - lazy loaded (JS downloaded only when navigated to)
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const Settings = lazy(() => import("./components/Setting/Settings"));
const Users = lazy(() => import("./components/User/Users"));
const Premises = lazy(() => import("./components/Premise/Premises"));
const Report = lazy(() => import("./components/Report/Report"));
const Timing = lazy(() => import("./components/Report/Timing/Timing"));
const BreakAndViolation = lazy(() => import("./components/Report/BreakAndViolation/BreakAndViolation"));
const PremiseList = lazy(() => import("./components/Premise/PremiseList"));
const BreakAndViolationReport = lazy(() => import("./components/Report/BreakAndViolation/BreakAndViolationReport"));
const PremiseAction = lazy(() => import("./components/Premise/PremiseAction"));
const UserAction = lazy(() => import("./components/User/UserAction"));
const TimeOffPolicies = lazy(() => import("./components/Setting/TimeOffConfiguration/TimeOffPolicies"));
const TimeOffConfiguration = lazy(() => import("./components/Setting/TimeOffConfiguration/TimeOffConfiguration"));
const Holidays = lazy(() => import("./components/Setting/Holidays/Holidays"));
const AddExistingUserAction = lazy(() => import("./components/User/AddExistingUserAction"));
const Statistics = lazy(() => import("./components/User/Statistics/Statistics"));
const TimingReport = lazy(() => import("./components/Report/Timing/TimingReport"));
const LeaveReport = lazy(() => import("./components/Report/Leave/LeaveReport"));
const Profile = lazy(() => import("./components/User/Profile"));
const TimePermissionReport = lazy(() => import("./components/Report/TimePermission/TimePermissionReport"));
const SubscriptionPlanDetails = lazy(() => import("./components/Subscription/SubscriptionPlanDetails"));
const SubscriptionCheckOut = lazy(() => import("./components/Subscription/SubscriptionCheckOut"));
const Payments = lazy(() => import("./components/Invoice/Payments.js"));
const SubscriptionPaymentDetails = lazy(() => import("./components/Subscription/SubscriptionPaymentDetails.js"));
const SubscriptionSummary = lazy(() => import("./components/Invoice/SubscriptionSummary.js"));
const WorkShiftTemplate = lazy(() => import("./components/WorkShifts/WorkShiftTemplate.js"));
const StaffShift = lazy(() => import("./components/WorkShifts/StaffShift.js"));
const DefineWorkLocation = lazy(() => import("./components/WorkLocations/DefineWorkLocation.js"));
const AssignLocationToStaff = lazy(() => import("./components/WorkLocations/AssignLocationToStaff.js"));
const PayrollConnect = lazy(() => import("./components/Setting/IntutitPayrollConnect/PayrollConnect.js"));
const TimeLogEntry = lazy(() => import("./components/Setting/IntutitPayrollConnect/TimeLogEntry/TimeLogEntry.js"));
const APISettings = lazy(() => import("./components/Setting/APISettings.js"));
const AIPage = lazy(() => import("./components/AI/AIPage.js"));
const Payroll = lazy(() => import("./components/Payroll/Payroll.js"));

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // const token = sessionStorage.getItem('authToken');
    const token = localStorage.getItem("authToken");
    // console.log("Token found:", token);
    setIsAuthenticated(!!token);
    // console.log = () => {};
  }, []);

  return (
    <ToastProvider>
      <Router>
        <div className="App">
          <PendingPaymentNotice isAuthenticated={isAuthenticated} />
          <Suspense fallback={<div></div>}>
          <Routes>
            <Route path="/" element={<Navigate to="/signin" />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/email/verify/:id/:hash" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/otp-verification" element={<OtpVerification />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/signin" element={<Login />} />
            {/* Payment Gateway Routes  */}

            {/* Payment Gateway Functionality Start  */}

            {/* <Route
              path="/subscription/plan-details"
              element={<SubscriptionPlanDetails />}
            />
            <Route
              path="/subscription/checkout"
              element={<SubscriptionCheckOut />}
            />
            <Route
              path="/subscription/payment-details"
              element={<SubscriptionPaymentDetails />}
            /> */}

            {/* Payment Gateway Functionality End */}

            {/* Dashboard Routes  */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Dashboard />
                </PrivateRoute>
              }
            />

            {/* Premises Routes  */}

            <Route
              path="/premises"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Premises />
                </PrivateRoute>
              }
            />
            <Route
              path="/premise/create"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <PremiseAction />
                </PrivateRoute>
              }
            />
            <Route
              path="/premise/update"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <PremiseAction />
                </PrivateRoute>
              }
            />
            {/* Users Routes  */}
            <Route
              path="/users"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Users />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/create"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <UserAction />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/update"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <UserAction />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/existing/add"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <AddExistingUserAction />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/statistics"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Statistics />
                </PrivateRoute>
              }
            />

            {/* WorkShift Routes  */}
            <Route
              path="/workshift/defineshifts"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <WorkShiftTemplate />
                </PrivateRoute>
              }
            />
            <Route
              path="/workshift/staffshifts"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <StaffShift />
                </PrivateRoute>
              }
            />

            {/* WorkLocation Routes  */}
            <Route
              path="/worklocation/definelocation"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <DefineWorkLocation />
                </PrivateRoute>
              }
            />

            <Route
              path="/worklocation/assignlocationtostaff"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <AssignLocationToStaff />
                </PrivateRoute>
              }
            />

            {/* Report Routes  */}
            <Route
              path="/report"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Report />
                </PrivateRoute>
              }
            />
            <Route
              path="/report/timing"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Timing />
                </PrivateRoute>
              }
            />

            <Route
              path="/report/break-violation"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <BreakAndViolationReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/report/leave"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <LeaveReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/report/timepermission"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TimePermissionReport />
                </PrivateRoute>
              }
            />

            {/* Setting Routes  */}
            <Route
              path="/settings"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/time-off/policy"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TimeOffPolicies />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/time-off/policy/create"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TimeOffConfiguration />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/time-off/policy/view"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TimeOffConfiguration />
                </PrivateRoute>
              }
            />

            <Route
              path="/settings/holidays"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Settings />
                </PrivateRoute>
              }
            />

            {/* Payroll Routes  */}
            <Route
              path="/settings/payroll"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <PayrollConnect />
                </PrivateRoute>
              }
            />

                        {/* Payroll Routes  */}
            <Route
              path="/ai"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <AIPage />
                </PrivateRoute>
              }
            />

            {/* API Settings Routes  */}
            <Route
              path="/settings/api"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <APISettings />
                </PrivateRoute>
              }
            />

            {/* India Payroll Routes  */}
            <Route
              path="/payroll"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payroll />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/run"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payroll />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/employees"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payroll />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/history"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payroll />
                </PrivateRoute>
              }
            />
            <Route
              path="/payroll/settings"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payroll />
                </PrivateRoute>
              }
            />

            <Route
              path="/settings/payroll/addTimeLog"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TimeLogEntry />
                </PrivateRoute>
              }
            />

            {/* Account Routes  */}
            <Route
              path="/account/profile"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Profile />
                </PrivateRoute>
              }
            />

            {/* Payment Gateway Routes  */}

            {/* Payment Gateway Functionality Start */}

            <Route
              path="/account/payments"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <Payments />
                </PrivateRoute>
              }
            />
            {/*
            <Route
              path="/account/subscription"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <SubscriptionSummary />
                </PrivateRoute>
              }
            />
            */}

            {/* Payment Gateway Functionality End  */}
          </Routes>
          </Suspense>
        </div>
      </Router>
    </ToastProvider>
  );
};

export default App;
